import { InputBaseComponentProps } from '@mui/material';
import { ChangeEvent, forwardRef } from 'react';
import {
  NumberFormatValues,
  NumericFormat,
  NumericFormatProps,
  PatternFormat,
} from 'react-number-format';

/**
 * Props for the CustomNumberFormat component.
 * @extends Omit<NumericFormatProps, 'onChange'>
 */
export interface CustomNumberFormatProps
  extends
    Omit<NumericFormatProps, 'onValueChange'>,
    Omit<InputBaseComponentProps, 'onChange'> {
  /** Callback function for handling input changes */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Whether to allow negative numbers */
  allowNegative?: boolean;
  /** Whether the current value is negative */
  isNegative?: boolean;
  /** Number of decimal places to display */
  decimalScale?: number;
  thousandSeparator?: boolean;
  textAlign?: 'left' | 'right';
  maxLength?: number;
  maxAmount?: number;
  minAmount?: number;
  format?: string;
  mask?: string;
  min?: number;
  isAllowed?: ((values: NumberFormatValues) => boolean) | undefined;
  getValue?: (values: number | undefined) => void;
}

/**
 * A custom number format component that wraps react-number-format.
 * It provides additional functionality for handling negative numbers and decimal places.
 */
const CustomNumberFormat = forwardRef<
  HTMLInputElement,
  CustomNumberFormatProps
>(function CustomNumberFormat(props, ref) {
  const {
    onChange,
    allowNegative,
    isNegative,
    decimalScale = 2,
    thousandSeparator = true,
    textAlign = 'right',
    maxLength = 16,
    maxAmount,
    minAmount,
    min,
    format,
    mask,
    isAllowed,
    getValue,
    ...other
  } = props as CustomNumberFormatProps;

  const FormatComponent = format ? PatternFormat : NumericFormat;

  return (
    <FormatComponent
      {...other}
      format={format ?? ''}
      mask={mask}
      getInputRef={ref}
      onValueChange={(values) => {
        const { value } = values;

        if (!value) {
          onChange({
            target: { value: '' },
          } as ChangeEvent<HTMLInputElement>);
          return;
        }

        const numericValue = Number(value);

        const finalValue = isNegative
          ? String(-Math.abs(numericValue))
          : String(numericValue);

        onChange({
          target: { value: finalValue },
        } as ChangeEvent<HTMLInputElement>);
      }}
      isAllowed={(values) => {
        const { floatValue, value } = values;

        getValue?.(floatValue);

        if (maxLength && value.length > maxLength) {
          return false;
        }

        if (floatValue === undefined) return true;

        // Block typing if exceeds max
        if (maxAmount !== undefined && floatValue > maxAmount) {
          onChange({
            target: { value: String(maxAmount) },
          } as ChangeEvent<HTMLInputElement>);
          return false;
        }
        // Optional: block below min
        if (floatValue !== undefined) {
          const minLimit = minAmount ?? min;

          if (minLimit !== undefined && floatValue < minLimit) {
            onChange({
              target: { value: String(minLimit) },
            } as ChangeEvent<HTMLInputElement>);
            return false;
          }
        }

        return isAllowed ? isAllowed(values) : true;
      }}
      valueIsNumericString
      thousandSeparator={thousandSeparator}
      fixedDecimalScale
      decimalScale={decimalScale}
      allowNegative={allowNegative}
      prefix={isNegative ? '-' : ''}
      maxLength={maxLength}
      style={{ textAlign }}
    />
  );
});

export default CustomNumberFormat;
